import { __decorate } from "tslib";
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { PER_PAGE } from '@/helpers/constants/app';
import { last, union } from 'lodash';
import { isNotEmpty } from '@/helpers';
/**
 * Paginator Component
 * @since 0.1.0
 *
 * How Window Based Pagination Works with relay ?
 * We only need to use `after` and `first` as graphql query params
 * We need to create an array data property(cursorStack) where we store our
 *  list of cursors(endCursor)
 * Everytime we click next button we push `endCursor` into cursorStack
 * Everytime we click previous button we `remove` last cursor in our stack
 *  and pass `new` last cursor in our stack which is our second to the last
 *    paginated records so we can refetch previous record :) Happy Hacking
 *
 * @see https://engineering.dubsmash.com/bi-directional-pagination-using-graphql-relay-b523c919c96
 */
let Paginator = class Paginator extends Vue {
    constructor() {
        super(...arguments);
        this.cursorStack = [];
        this.start = 1;
    }
    onReset(value) {
        if (value) {
            this.start = 1;
            this.cursorStack = [];
            this.$emit('startChanged', this.start);
        }
        this.$emit('pageReset');
    }
    cursorStart() {
        this.$emit('startChanged', this.start);
        // remove 2nd condition if remember last page cursor will implement in other pagination
        if (this.getCursorStorage() &&
            (this.pageTitle === 'Products' || this.pageTitle === 'My In-App Stores')) {
            this.start = this.getCursorStorage().length * this.perPage + 1;
            return this.start;
        }
        return this.start;
    }
    hasPreviousPage() {
        // remove 2nd condition if remember last page cursor will implement in other pagination
        if (this.getCursorStorage() &&
            (this.pageTitle === 'Products' || this.pageTitle === 'My In-App Stores')) {
            return isNotEmpty(this.getCursorStorage().length);
        }
        return isNotEmpty(this.cursorStack.length);
    }
    get hasNextPage() {
        return this.pageInfo.hasNextPage;
    }
    get displayPagination() {
        return this.total > this.perPage;
    }
    /**
     * Display total paginated records
     * We assume that we have an initial paginated record by default, thats why
     * We add `1` into cursorStack length and multiply these into paginated
     *  records per request
     * TODO: Improve transition
     */
    paginatedRecords() {
        let stackCount = (this.cursorStack.length + 1) * this.perPage;
        if (!this.hasNextPage) {
            return this.total;
        }
        // remove 2nd condition if remember last page cursor will implement in other pagination
        if (this.getCursorStorage() &&
            (this.pageTitle === 'Products' || this.pageTitle === 'My In-App Stores')) {
            stackCount = (this.getCursorStorage().length + 1) * this.perPage;
            return stackCount;
        }
        return stackCount;
    }
    onNavigatingBack() {
        this.cursorStack.pop();
        const cursors = this.getCursorStorage();
        if (cursors) {
            cursors.pop();
            this.setCursorStorage(cursors);
        }
        this.start -= this.perPage;
        this.$emit('back', last(this.cursorStack));
    }
    onFetchingMore() {
        const { endCursor } = this.pageInfo;
        this.cursorStack = union(this.cursorStack, [endCursor]);
        this.start += this.perPage;
        const cursors = union(this.getCursorStorage(), [endCursor]);
        this.setCursorStorage(cursors);
        this.$emit('more', endCursor);
    }
    cursorName() {
        return `${this.pageTitle.toLowerCase()}_cursor`;
    }
    getCursorStorage() {
        return JSON.parse(localStorage.getItem(this.cursorName()));
    }
    setCursorStorage(cursors) {
        localStorage.setItem(this.cursorName(), JSON.stringify(cursors));
    }
};
__decorate([
    Prop({ type: Boolean, required: false })
], Paginator.prototype, "small", void 0);
__decorate([
    Prop({ type: Object, required: true })
], Paginator.prototype, "pageInfo", void 0);
__decorate([
    Prop({ type: Number, default: 0 })
], Paginator.prototype, "total", void 0);
__decorate([
    Prop({ type: Number, default: PER_PAGE })
], Paginator.prototype, "perPage", void 0);
__decorate([
    Prop({ type: String, default: '' })
], Paginator.prototype, "pageTitle", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], Paginator.prototype, "reset", void 0);
__decorate([
    Prop({ type: String, default: 'paginator-next' })
], Paginator.prototype, "dataCyNext", void 0);
__decorate([
    Prop({ type: String, default: 'paginator-back' })
], Paginator.prototype, "dataCyBack", void 0);
__decorate([
    Watch('reset')
], Paginator.prototype, "onReset", null);
Paginator = __decorate([
    Component
], Paginator);
export default Paginator;
